import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';
import WCLCLogo from '../ui/WCLCLogo';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  {
    name: 'Get Involved',
    path: '/get-involved',
    children: [
      { name: 'Founding Members', path: '/founding-members' },
      { name: 'Membership', path: '/membership' },
      { name: 'Volunteer', path: '/get-involved#volunteer' },
      { name: 'Donate', path: '/donate' },
      { name: 'Partner With Us', path: '/get-involved#partner' },
      { name: 'About GFWC', path: '/gfwc' },
    ],
  },
  { name: 'Programs', path: '/programs' },
  { name: 'Events', path: '/events' },
  { name: 'News', path: '/news' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Streamlined Header with elegant text */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 flex justify-center">
          <Link to="/" className="group">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#3c4a2a] tracking-[0.15em] sm:tracking-[0.2em] uppercase transition-opacity duration-300 group-hover:opacity-80"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              The Women's Club of Lincoln County
            </h1>
          </Link>
        </div>
        {/* Divider line */}
        <div 
          className="h-[2px] sm:h-[3px] w-full" 
          style={{ backgroundColor: '#9dad84' }}
        />
        
        {/* Navigation */}
        <div className="bg-white py-2 sm:py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              {/* Desktop Navigation - Left aligned */}
              <nav className="hidden lg:flex items-center">
                <NavigationMenu>
                  <NavigationMenuList className="gap-1">
                    {navItems.map((item) =>
                      item.children ? (
                        <NavigationMenuItem key={item.name}>
                          <NavigationMenuTrigger
                            className={cn(
                              'text-sm font-medium transition-colors bg-transparent',
                              isActive(item.path) 
                                ? 'text-[#5d6b3c]' 
                                : 'text-[#3c4a2a] hover:text-[#5d6b3c]'
                            )}
                          >
                            {item.name}
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            <ul className="grid w-48 gap-1 p-2 bg-white border border-[#5f6d51]/10">
                              {item.children.map((child) => (
                                <li key={child.name}>
                                  <NavigationMenuLink asChild>
                                    <Link
                                      to={child.path}
                                      className="block select-none rounded-md p-3 text-sm leading-none no-underline outline-none transition-colors hover:bg-[#f1e3eb] text-[#3c4a2a] hover:text-[#5d6b3c]"
                                    >
                                      {child.name}
                                    </Link>
                                  </NavigationMenuLink>
                                </li>
                              ))}
                            </ul>
                          </NavigationMenuContent>
                        </NavigationMenuItem>
                      ) : (
                        <NavigationMenuItem key={item.name}>
                          <Link
                            to={item.path}
                            className={cn(
                              'inline-flex h-9 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors',
                              isActive(item.path)
                                ? 'text-[#5d6b3c]'
                                : 'text-[#3c4a2a] hover:text-[#5d6b3c]'
                            )}
                          >
                            {item.name}
                          </Link>
                        </NavigationMenuItem>
                      )
                    )}
                  </NavigationMenuList>
                </NavigationMenu>
              </nav>

              {/* Desktop CTA Buttons - Right aligned */}
              <div className="hidden lg:flex items-center space-x-3">
                {user?.role === 'admin' && (
                  <Link to="/admin">
                    <Button
                      variant="ghost"
                      data-testid="header-admin-btn"
                      className="text-[#5f6d51] hover:text-[#5d6b3c] hover:bg-[#f1e3eb]"
                    >
                      Admin
                    </Button>
                  </Link>
                )}
                <Link to={user ? '/member-dashboard' : '/login'}>
                  <Button
                    variant="ghost"
                    data-testid="header-member-login-btn"
                    className="text-[#5f6d51] hover:text-[#5d6b3c] hover:bg-[#f1e3eb]"
                  >
                    {user ? 'My Portal' : 'Member Login'}
                  </Button>
                </Link>
                <Link to="/donate">
                  <Button className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-5">
                    <Heart className="w-4 h-4 mr-2" />
                    Donate
                  </Button>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="lg:hidden ml-auto">
                  <Button variant="ghost" size="icon" className="text-[#3c4a2a]">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 bg-[#f1e3eb]">
                  <SheetTitle className="font-serif text-xl text-[#3c4a2a] mb-6">
                    Menu
                  </SheetTitle>
                  <nav className="flex flex-col space-y-1">
                    {navItems.map((item) => (
                      <div key={item.name}>
                        <Link
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={cn(
                            'block py-3 px-4 text-base font-medium rounded-lg transition-colors',
                            isActive(item.path)
                              ? 'bg-white text-[#5d6b3c]'
                              : 'text-[#3c4a2a] hover:bg-white/50'
                          )}
                        >
                          {item.name}
                        </Link>
                        {item.children && (
                          <div className="ml-4 mt-1 space-y-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.path}
                                onClick={() => setMobileMenuOpen(false)}
                                className="block py-2 px-4 text-sm text-[#5f6d51] hover:text-[#5d6b3c]"
                              >
                                {child.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </nav>
                  <div className="mt-8 space-y-3">
                    <Link to={user ? '/member-dashboard' : '/login'} onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        variant="outline"
                        data-testid="mobile-member-login-btn"
                        className="w-full border-[#5d6b3c] text-[#5d6b3c]"
                      >
                        {user ? 'My Portal' : 'Member Login'}
                      </Button>
                    </Link>
                    <Link to="/donate" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
                        <Heart className="w-4 h-4 mr-2" />
                        Donate
                      </Button>
                    </Link>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
      
      {/* Spacer for fixed header */}
      <div className="h-[120px] sm:h-[130px]" />
    </>
  );
};

export default Header;
